import {
  GoBackProps,
  GoForwardProps,
  IExternalCallbacks,
  OnBeforeTextRenderProps,
  OnNavHistoryStackChangedProps,
} from '@dorian/creation-tools-ui';
import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { BranchStep } from '../../../../dorian-shared/types/branch/BranchStep';
import { Character } from '../../../../dorian-shared/types/character/Character';
import { IAchievementGetDTO } from '../../../../dorian-shared/types/achievement/Achievement';
import { ChoiceMemory } from '../../../../services/choiceMemory/ChoiceMemory';
import { logger } from '../../../../services/loggerService/loggerService';
import { processAudios, stopAllAudio } from '../../../pages/AudioStepSettingsModal/AudioPlay/AudioController';
import { onBeforeTextRender } from './onbeforeTextRender';
import { onGoBackStack } from './onGoBackStack';
import { onGoForwardStack } from './onGoForwardStack';
import { resolveVariableValue } from './resolveVariableValue';

let storedStepAudios: any = null;

export function replaySounds() {
  if (storedStepAudios !== null) {
    processAudios(storedStepAudios);
  }
}

export function setStoredStepAudios(stepAudios: any[] | null): void {
  storedStepAudios = stepAudios;
}

export function useChoiceMemoryCallbacks(
  choiceMemory: ChoiceMemory | undefined,
  popupComponent: (text: string, primaryButtonText?: string, iconId?: number) => Promise<void>,
  scrollToBranch?: (branchId: number) => void,
  characters?: Character[],
  isControlled?: boolean,
  // PlayerHas checks ask the author Yes/No; achievements supply reward names.
  confirmPlayerHas?: (text: string) => Promise<boolean>,
  achievements: IAchievementGetDTO[] = [],
) {
  const [errorMessage, setErrorMessage] = useState('');
  const [branchStep, setBranchStep] = useState<BranchStep | undefined>(undefined);
  const navHistoryStacksRef = useRef<OnNavHistoryStackChangedProps['navHistoryStacks']>([]);

  const handleBeforeTextRender = useCallback(
    (props: OnBeforeTextRenderProps) => {
      const { text, step } = props;
      setErrorMessage('');
      try {
        return onBeforeTextRender({
          choiceMemory,
          text,
          step,
          characters,
          isControlled,
        });
      } catch (e) {
        if (e instanceof Error) {
          setErrorMessage(`Error in onBeforeTextRender: ${e.message}`);
        } else {
          setErrorMessage('Error in onBeforeTextRender');
        }
        return text;
      }
    },
    [characters, choiceMemory, isControlled],
  );

  const handleGoForward = useCallback(async (props: GoForwardProps): Promise<number | 'goAhead' | 'goBack' | undefined> => {
    const { episodeId, step, stepNum } = props;
    const currentStep = step as unknown as BranchStep;
    setErrorMessage('');
    try {
      processAudios(currentStep.stepAudios);
      storedStepAudios = currentStep.stepAudios || null;
      return await onGoForwardStack(choiceMemory, episodeId, currentStep, stepNum, popupComponent, confirmPlayerHas, achievements);
    } catch (e) {
      if (e instanceof Error) {
        setErrorMessage(`Error in onGoForwardStack: ${e.message}`);
      } else {
        setErrorMessage('Error in onGoForwardStack');
      }
      return undefined;
    }
  }, [choiceMemory, popupComponent, confirmPlayerHas, achievements]);

  const handleGoBack = useCallback((props: GoBackProps): number | 'goAhead' | 'goBack' | undefined => {
    const { historyItem } = props;
    setErrorMessage('');
    try {
      stopAllAudio();
      storedStepAudios = null;
      return onGoBackStack(choiceMemory, historyItem);
    } catch (e) {
      if (e instanceof Error) {
        setErrorMessage(`Error in onGoBackStack: ${e.message}`);
      } else {
        setErrorMessage('Error in onGoBackStack');
      }
      return undefined;
    }
  }, [choiceMemory]);

  const handleResolveVariableValue = useCallback(
    (key: string) => resolveVariableValue(choiceMemory, key),
    [choiceMemory],
  );

  if (errorMessage) {
    logger.error('ERROR:', errorMessage);
  }

  const handleExternalCallbacks = useMemo((): IExternalCallbacks => ({
    resolveVariableValue: handleResolveVariableValue,
    onBeforeTextRender: handleBeforeTextRender,
    onNavHistoryStackChanged: async (props: OnNavHistoryStackChangedProps) => {
      const { step, navHistoryStacks } = props;
      setBranchStep(step as unknown as BranchStep);

      scrollToBranch?.(step?.branchId ?? 0);
      const lastStackItem = navHistoryStacks[navHistoryStacks.length - 1];

      if (navHistoryStacksRef.current.length > navHistoryStacks.length) {
        navHistoryStacksRef.current = navHistoryStacks;
        return handleGoBack({ historyItem: lastStackItem });
      }

      if (navHistoryStacksRef.current.length < navHistoryStacks.length) {
        navHistoryStacksRef.current = navHistoryStacks;
        if (!step) {
          return undefined;
        }
        return await handleGoForward({
          episodeId: lastStackItem.episodeId,
          step,
          stepNum: lastStackItem.stepNum,
        });
      }
      return undefined;
    },
  }), [handleBeforeTextRender, handleGoBack, handleGoForward, handleResolveVariableValue, scrollToBranch]);

  return {
    handleExternalCallbacks,
    errorMessage,
    branchStep,
  };
}
